import { Fragment, useEffect, useState } from "react";
import api, { fmtDateTime } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav, amdenNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldAlert, ChevronDown, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { roleLabel } from "@/lib/role";
import { actionLabel, actionSection, SECTIONS, actionsIn, ALL_ACTIONS, changes, searchActions } from "@/lib/auditLabels";

const PAGE = 100;

const Filter = ({ label, value, onChange, testid, children, width = "w-48" }) => (
  <div><Label className="text-xs">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={`mt-1 h-10 ${width}`}><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-80">{children}</SelectContent>
    </Select>
  </div>
);

function Changes({ r }) {
  const list = changes(r.before, r.after);
  return (
    <div className="px-6 py-3 bg-zinc-50 text-xs space-y-2" data-testid={`audit-changes-${r.id}`}>
      {r.reason && <div><span className="text-zinc-500">Reason: </span>{r.reason}</div>}
      {r.amden_support && <div className="text-amber-700">Done by Amden support while signed in to this company{r.impersonation?.as_name ? ` as ${r.impersonation.as_name}` : ""}.</div>}
      {list.length === 0 ? <div className="text-zinc-400">No field-level details were recorded for this action.</div> : (
        <table className="w-full max-w-3xl"><thead><tr className="text-left text-zinc-500"><th className="py-1 pr-4">Field</th>{r.before && <th className="py-1 pr-4">Before</th>}{r.after && <th className="py-1">After</th>}</tr></thead>
          <tbody>{list.slice(0, 40).map((c) => (
            <tr key={c.field} className="border-t border-zinc-200 align-top"><td className="py-1 pr-4 capitalize text-zinc-600">{c.field}</td>
              {r.before && <td className="py-1 pr-4 text-rose-700 break-all">{c.from}</td>}{r.after && <td className="py-1 text-emerald-700 break-all">{c.to}</td>}</tr>))}</tbody>
        </table>)}
    </div>
  );
}

export default function AuditLog() {
  const { user } = useAuth();
  const amden = user?.role === "amden_super";
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [people, setPeople] = useState([]);
  const [f, setF] = useState({ company: "all", section: "all", action: "all", who: "all", start: "", end: "", q: "", support: false });
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v, ...(k === "section" ? { action: "all" } : {}), ...(k === "company" ? { who: "all" } : {}) }));

  useEffect(() => { if (amden) { api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}); } }, [amden]);
  useEffect(() => {
    api.get("/audit-review/people", { params: { company_id: f.company !== "all" ? f.company : undefined } }).then((r) => setPeople(r.data)).catch(() => {});
  }, [f.company]);

  const params = (skip) => ({
    company_id: f.company !== "all" ? f.company : undefined,
    user_id: f.who !== "all" ? f.who : undefined,
    actions: f.action !== "all" ? f.action : f.section !== "all" ? actionsIn(f.section).join(",") : undefined,
    start: f.start || undefined, end: f.end || undefined, q: f.q || undefined, q_actions: searchActions(f.q).join(",") || undefined, support_only: f.support || undefined, limit: PAGE, skip,
  });
  const load = async (more = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit-review", { params: params(more ? rows.length : 0) });
      setRows(more ? [...rows, ...data.rows] : data.rows); setTotal(data.total);
    } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(() => load(false), 250); return () => clearTimeout(t); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps

  const actionOpts = f.section === "all" ? ALL_ACTIONS : actionsIn(f.section);
  const clear = () => setF({ company: f.company, section: "all", action: "all", who: "all", start: "", end: "", q: "", support: false });

  return (
    <AdminLayout nav={amden ? amdenNav : adminNav} title={amden ? "Activity Review" : "Audit Log"}>
      <PageHeader title={amden ? "Activity Review" : "Audit Log"} subtitle={amden ? "Check exactly who did what, when, for any client" : "Every administrative and financial change"} />
      <div className="bg-white border border-zinc-200 rounded-md p-4 mb-4 flex flex-wrap gap-3 items-end" data-testid="audit-filters">
        {amden && <Filter label="Client" value={f.company} onChange={(v) => set("company", v)} testid="audit-company" width="w-56">
          <SelectItem value="all">All clients</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </Filter>}
        <Filter label="Section" value={f.section} onChange={(v) => set("section", v)} testid="audit-section">
          <SelectItem value="all">All sections</SelectItem>{SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </Filter>
        <Filter label="Action" value={f.action} onChange={(v) => set("action", v)} testid="audit-action" width="w-60">
          <SelectItem value="all">All actions</SelectItem>{actionOpts.map((a) => <SelectItem key={a} value={a}>{actionLabel(a)}</SelectItem>)}
        </Filter>
        <Filter label="Done by" value={f.who} onChange={(v) => set("who", v)} testid="audit-who" width="w-56">
          <SelectItem value="all">Anyone</SelectItem>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({roleLabel(p.role)}){p.email ? ` · ${p.email}` : ""}</SelectItem>)}
        </Filter>
        <div><Label className="text-xs">From</Label><Input data-testid="audit-start" type="date" value={f.start} onChange={(e) => set("start", e.target.value)} className="mt-1 h-10 w-40" /></div>
        <div><Label className="text-xs">To</Label><Input data-testid="audit-end" type="date" value={f.end} onChange={(e) => set("end", e.target.value)} className="mt-1 h-10 w-40" /></div>
        <div><Label className="text-xs">Search</Label><Input data-testid="audit-search" placeholder="Name, reason…" value={f.q} onChange={(e) => set("q", e.target.value)} className="mt-1 h-10 w-48" /></div>
        {amden && <label className="flex items-center gap-2 text-sm h-10"><input data-testid="audit-support-only" type="checkbox" checked={f.support} onChange={(e) => set("support", e.target.checked)} />Amden support only</label>}
        <Button data-testid="audit-clear" variant="ghost" size="sm" onClick={clear}>Clear</Button>
      </div>
      <div className="text-xs text-zinc-500 mb-2" data-testid="audit-count">{loading ? "Loading…" : `${total} matching action${total === 1 ? "" : "s"}`}</div>
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto kqm-scroll">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            <th className="p-3 w-6"></th><th className="p-3">When</th>{amden && <th className="p-3">Client</th>}<th className="p-3">Who</th><th className="p-3">What they did</th><th className="p-3">To</th><th className="p-3">Section</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.id}>
                <tr data-testid={`audit-row-${r.id}`} onClick={() => setOpen(open === r.id ? null : r.id)} className="border-b border-zinc-50 hover:bg-zinc-50 cursor-pointer">
                  <td className="p-3 text-zinc-400">{open === r.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                  <td className="p-3 num text-xs whitespace-nowrap">{fmtDateTime(r.timestamp)}</td>
                  {amden && <td className="p-3 text-xs text-zinc-600">{r.company_name || "Amden platform"}</td>}
                  <td className="p-3 whitespace-nowrap">{r.user_name || "System"} <span className="text-xs text-zinc-400">({roleLabel(r.user_role || "system")})</span></td>
                  <td className="p-3 font-medium" data-testid={`audit-action-${r.id}`}>{actionLabel(r.action)}</td>
                  <td className="p-3 text-zinc-600 max-w-xs truncate" data-testid={`audit-subject-${r.id}`}>{r.subject || "—"}</td>
                  <td className="p-3 text-xs text-zinc-500 whitespace-nowrap">{actionSection(r.action)}</td>
                  <td className="p-3">{r.amden_support && <ShieldAlert className="h-4 w-4 text-amber-500" title="Amden support" />}</td>
                </tr>
                {open === r.id && <tr><td colSpan={amden ? 8 : 7} className="p-0"><Changes r={r} /></td></tr>}
              </Fragment>
            ))}
            {!loading && rows.length === 0 && <tr><td colSpan={amden ? 8 : 7} className="p-8 text-center text-zinc-400">No actions match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      {rows.length < total && <div className="text-center mt-4"><Button data-testid="audit-load-more" variant="outline" disabled={loading} onClick={() => load(true)}>Load more ({total - rows.length} left)</Button></div>}
    </AdminLayout>
  );
}
